import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";

export const SettingsDialog = ({ open, onOpenChange, onPurged }) => {
  const [s, setS] = useState(null);

  useEffect(() => {
    if (open) api.get("/admin/settings").then((r) => setS(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [open]);

  const save = async () => {
    const days = [s.inquiry_retention_days, s.application_retention_days].map(Number);
    if (days.some((d) => !Number.isInteger(d) || d < 30 || d > 3650)) {
      toast.error("Retention periods must be whole numbers between 30 and 3650 days.");
      return;
    }
    try {
      const { data } = await api.put("/admin/settings", {
        inquiry_retention_days: Number(s.inquiry_retention_days),
        application_retention_days: Number(s.application_retention_days),
        auto_purge: s.auto_purge,
        calendly_url: s.calendly_url || "",
      });
      setS(data);
      toast.success("Retention settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const purge = async () => {
    try {
      const { data } = await api.post("/admin/retention/run");
      toast.success(`Retention cleanup removed ${data.deleted} record(s)`);
      onPurged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="admin-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Settings</DialogTitle>
          <DialogDescription>Closed inquiries and closed applicant records (Not Proceeding / Withdrawn) older than these periods are deleted automatically on server start, or when you run cleanup.</DialogDescription>
        </DialogHeader>
        {s && (
          <div className="space-y-5">
            <label className="block space-y-2 text-sm font-semibold">Calendly booking link (used in interview invitations)
              <Input type="url" placeholder="https://calendly.com/your-link" value={s.calendly_url || ""} onChange={(e) => setS({ ...s, calendly_url: e.target.value })} data-testid="admin-calendly-input" />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="space-y-2 text-sm font-semibold">Inquiries (days)
                <Input type="number" min={30} max={3650} value={s.inquiry_retention_days} onChange={(e) => setS({ ...s, inquiry_retention_days: e.target.value })} data-testid="admin-retention-inquiry-input" />
              </label>
              <label className="space-y-2 text-sm font-semibold">Applications (days)
                <Input type="number" min={30} max={3650} value={s.application_retention_days} onChange={(e) => setS({ ...s, application_retention_days: e.target.value })} data-testid="admin-retention-application-input" />
              </label>
            </div>
            <label className="flex items-center justify-between text-sm font-semibold">Automatic cleanup
              <Switch checked={s.auto_purge} onCheckedChange={(v) => setS({ ...s, auto_purge: v })} data-testid="admin-retention-auto-switch" />
            </label>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={save} className="btn-pill btn-primary !py-2" data-testid="admin-retention-save-button">Save settings</button>
              <button type="button" onClick={purge} className="btn-pill btn-outline !py-2" data-testid="admin-retention-run-button">Run cleanup now</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
